import {
  InputNumber as $InputNumber,
  Space,
  type InputNumberProps as $InputNumberProps,
} from "antd";
import { useCallback, type CSSProperties, type ReactNode } from "react";

import { Popover, type PopoverProps } from "../shared/Popover";

// Helper function for table navigation
export const findCellWithInput = (
  cell: HTMLTableCellElement,
  getNextCell: (cell: HTMLTableCellElement) => HTMLTableCellElement | null
): HTMLTableCellElement | null => {
  let currentCell = getNextCell(cell);
  while (currentCell) {
    const input = currentCell.querySelector("input");
    // Only navigate to InputNumber inputs
    if (input && !input.classList.contains("ant-checkbox-input")) {
      return currentCell;
    }
    currentCell = getNextCell(currentCell);
  }
  return null;
};

export type InputNumberProps = Omit<$InputNumberProps, "onChange"> & {
  addonBefore?: ReactNode;
  addonAfter?: ReactNode;
  per?: ReactNode;
  inputStyle?: CSSProperties;
  popoverProps?: PopoverProps | false;
  onChange?: (value?: number | null) => void;
  selectTextOnFocus?: boolean;
  enableArrowNavigation?: boolean;
};

export function InputNumber({
  style,
  inputStyle,
  className,
  addonBefore,
  addonAfter,
  per,
  popoverProps,
  onChange,
  selectTextOnFocus = true,
  onFocus,
  enableArrowNavigation = false,
  onKeyDown,
  ...restProps
}: InputNumberProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (enableArrowNavigation) {
        const navigationKeys = [
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
          "Enter",
        ];
        if (navigationKeys.includes(e.key)) {
          const input = e.currentTarget;
          const cell = input.closest("td");
          const row = cell?.closest("tr");

          if (cell && row) {
            let targetCell: HTMLTableCellElement | null = null;
            const cellIndex = Array.from(row.cells).indexOf(
              cell as HTMLTableCellElement
            );

            switch (e.key) {
              case "ArrowDown":
              case "Enter":
                targetCell = findCellWithInput(
                  cell as HTMLTableCellElement,
                  (c) => {
                    const currentRow = c.closest("tr");
                    const nextRow =
                      currentRow?.nextElementSibling as HTMLTableRowElement;
                    return nextRow?.cells[cellIndex] || null;
                  }
                );
                break;
              case "ArrowUp":
                targetCell = findCellWithInput(
                  cell as HTMLTableCellElement,
                  (c) => {
                    const currentRow = c.closest("tr");
                    const prevRow =
                      currentRow?.previousElementSibling as HTMLTableRowElement;
                    return prevRow?.cells[cellIndex] || null;
                  }
                );
                break;
              case "ArrowRight":
                targetCell = findCellWithInput(
                  cell as HTMLTableCellElement,
                  (c) => c.nextElementSibling as HTMLTableCellElement
                );
                break;
              case "ArrowLeft":
                targetCell = findCellWithInput(
                  cell as HTMLTableCellElement,
                  (c) => c.previousElementSibling as HTMLTableCellElement
                );
                break;
            }

            if (targetCell) {
              const nextInput = targetCell.querySelector("input");
              if (nextInput) {
                e.preventDefault();
                nextInput.focus();
                nextInput.select();
              }
            }

            if (e.key === "Enter") {
              // Prevent form submission even if there's no next cell
              e.preventDefault();
            }
          }
        }
      }

      onKeyDown?.(e);
    },
    [enableArrowNavigation, onKeyDown]
  );

  let input = (
    <$InputNumber
      style={{ width: "100%", ...inputStyle }}
      {...restProps}
      onChange={(val) =>
        // undefined values are dropped on submit, better to use null
        onChange && onChange(val == undefined ? null : Number(val))
      }
      onFocus={selectTextOnFocus ? (e) => e.target.select() : onFocus}
      keyboard={enableArrowNavigation ? false : undefined}
      onKeyDown={handleKeyDown}
    />
  );

  if (popoverProps) {
    input = (
      <Popover styles={{ root: { maxWidth: "400px" } }} {...popoverProps}>
        {input}
      </Popover>
    );
  }

  if (addonBefore || addonAfter || per) {
    return (
      <Space.Compact
        block={style?.width === "auto" ? undefined : true}
        className={className}
        style={style}
      >
        {addonBefore && (
          <span className="ant-input-group-addon">{addonBefore}</span>
        )}
        {input}
        {addonAfter && (
          <span className="ant-input-group-addon">{addonAfter}</span>
        )}
        {per && <span className="ant-input-group-addon">{per}</span>}
      </Space.Compact>
    );
  }

  return input;
}
